/**
 * useHorizonStrengthEval — strength-stratified horizon evaluation.
 *
 * Fetches /api/horizon-strength-eval once per session (module-level cache).
 * The payload scores realized directional edge per (window, strength-bin,
 * horizon) over ALL matched signals (not just the top-20), binned by how
 * strongly the matched analogs agreed on direction. Used to show that edge
 * rises with conviction — see EdgeByConviction.
 */
import { useEffect, useState } from "react";
import requests from "../requests";

export interface StrengthCurveEntry {
  horizon: number;
  count: number;
  win_rate: number;
  avg_return: number;
}

export interface StrengthBin {
  label: string;            // e.g. "60-70" (% of analogs in the called direction)
  lo: number;
  hi: number;
  n_signals: number;
  curve: StrengthCurveEntry[];
  best_edge: StrengthCurveEntry | null;  // null until the bin clears min_sample_size
}

export interface StrengthWindow {
  name: string;             // "2w" | "4w" | "3m" | "6m" | "1y"
  headline_horizon: number;
  strength_metric: string;
  bins: StrengthBin[];
  // Same bins split per asset class (equity/forex/crypto/futures) — so the conviction
  // ramp can be read equity-only, since pooled bins are inflated by trending FX/crypto.
  by_asset_class?: Record<string, { bins: StrengthBin[] }>;
  min_sample_size: number;
}

export interface HorizonStrengthEvalData {
  windows: Record<string, StrengthWindow>;
  methodology: string;
}

// Module-level cache shared across components.
// Default shape so a 200 with an empty/partial body ({} before the archive is scored)
// never leaves `windows` undefined — consumers do Object.keys(data.windows). Mirrors
// useOosValidation's EMPTY-merge.
const EMPTY: HorizonStrengthEvalData = { windows: {}, methodology: "" };
let _cache: HorizonStrengthEvalData | null = null;
let _inflight: Promise<void> | null = null;

export function useHorizonStrengthEval(): HorizonStrengthEvalData | null {
  const [data, setData] = useState<HorizonStrengthEvalData | null>(_cache);

  useEffect(() => {
    if (_cache) { setData(_cache); return; }

    if (!_inflight) {
      _inflight = requests.get("/api/horizon-strength-eval")
        .then(r => { _cache = { ...EMPTY, ...(r.data as Partial<HorizonStrengthEvalData>) }; })
        .catch(() => { _cache = EMPTY; })
        .finally(() => { setData(_cache); });
    } else {
      void _inflight.finally(() => { setData(_cache); });
    }
  }, []);

  return data;
}
